/* eslint-disable camelcase, dot-notation */

import api, { redirectToSso } from 'utils/api'
import config from 'config'
import { RELEASE_SEGMENTS } from './constants'
import { mockReleaseNotes, mockAdminReleaseNotes } from './mockData'

const SEGMENT_STORAGE_KEY = 'releaseNotesSegment'
const ROLE_STORAGE_KEY = 'releaseNotesRole'
const DEFAULT_SCOPE = 'control'
const RELEASE_NOTE_PARAM = 'release_note'
const isDevelopment = process.env.NODE_ENV === 'development'

export const getCurrentSegment = () =>
  localStorage.getItem(SEGMENT_STORAGE_KEY) || 'S1'

export const setCurrentSegment = (segment) => {
  localStorage.setItem(SEGMENT_STORAGE_KEY, segment)
}

export const getCurrentRole = () => localStorage.getItem(ROLE_STORAGE_KEY) || ''

export const setCurrentRole = (role) => {
  localStorage.setItem(ROLE_STORAGE_KEY, role)
}

const releaseNotesHeaders = () => {
  const segment = getCurrentSegment()
  const role = getCurrentRole()
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': 'ru',
    'X-Partner-Segment': segment,
  }
  if (role) headers['X-Partner-Role'] = role

  return headers
}

const requestWithSegment = (endpoint, options = {}) => {
  return api.request(
    endpoint,
    {
      ...options,
      headers: new Headers(releaseNotesHeaders()),
    },
    config.redirectUri
  )
}

const requestBlobWithSegment = (endpoint, options = {}) => {
  return api.request(
    endpoint,
    {
      ...options,
      headers: new Headers(releaseNotesHeaders()),
    },
    config.redirectUri,
    true
  )
}

const requestNoContentWithSegment = async (endpoint, options = {}) => {
  const response = await fetch(`${config.redirectUri}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers: new Headers(releaseNotesHeaders()),
  })

  if (response.status === 401) {
    redirectToSso()
    throw response
  }

  if (response.status > 207) throw response
}

function normalizeReleaseNote(item) {
  return {
    id: item.id,
    title: item.title,
    summary: item.description || '',
    body: item.text || '',
    product_scope: item.product_scope,
    priority: item.priority,
    status: item.status,
    audience_roles: item.audience_roles || [],
    audience_segments: item.audience_segments || ['S1', 'S2', 'S3', 'S4', 'S5'],
    attachments: item.screenshots || [],
    cta_label: item.cta_label,
    cta_url: item.cta_url,
    published_at: item.published_at,
    opened: item.is_read || false,
  }
}

export const fetchReleaseNotes = async (params = {}) => {
  const query = new URLSearchParams({
    scope: DEFAULT_SCOPE,
    ...params,
  }).toString()
  try {
    const result = await requestWithSegment(
      `/api/v2/release_notes${query ? `?${query}` : ''}`
    )
    const items = result?.release_notes || result || []
    if (Array.isArray(items)) return items.map(normalizeReleaseNote)
    if (isDevelopment) return mockReleaseNotes

    throw new Error('Unexpected release notes response')
  } catch (e) {
    if (!isDevelopment) throw e

    return mockReleaseNotes
  }
}

export const fetchUnreadCount = async () => {
  try {
    const result = await requestWithSegment(
      `/api/v2/release_notes/unread_count?scope=${DEFAULT_SCOPE}`,
      // Фоновый счётчик не стоит разлогина: 401 не уводит на SSO.
      { skipAuthRedirect: true }
    )
    return Number(result?.count || result?.unread_count || 0)
  } catch (e) {
    if (!isDevelopment) throw e

    return mockReleaseNotes.filter((i) => !i.opened).length
  }
}

export const openReleaseNote = async (id) => {
  try {
    await requestNoContentWithSegment(`/api/v2/release_notes/${id}/open`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  } catch (e) {
    if (!isDevelopment) throw e
  }
}

export const openAllReleaseNoteRequest = async () => {
  try {
    await requestNoContentWithSegment(`/api/v2/release_notes/open_all`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  } catch (e) {
    if (!isDevelopment) throw e
  }
}
export const sendReleaseFeedback = async ({ id, feedbackType, message }) => {
  try {
    /* eslint-disable camelcase */
    const payload = { message, feedback_type: feedbackType }
    /* eslint-enable camelcase */

    await requestWithSegment(`/api/v2/release_notes/${id}/feedback`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    return true
  } catch (e) {
    if (!isDevelopment) throw e

    return false
  }
}

function normalizeAdminReleaseNote(item) {
  return {
    id: item.id,
    title: item.title,
    summary: item.description || '',
    body: item.text || '',
    productScope: item.product_scope,
    audience_segments: item.audience_segments,
    total_reads: item?.total_reads || 0,
    possible_reads: item?.possible_reads || 0,
    priority: item.priority,
    status: item.status,
    publishedAt: item.published_at,
    scheduledAt: item.scheduled_at,
    screenshots: item.screenshots || [],
  }
}

function paginateMockAdminReleaseNotes({ page, perPage, status }) {
  const filtered =
    status && status !== 'all'
      ? mockAdminReleaseNotes.filter((item) => item.status === status)
      : mockAdminReleaseNotes
  const start = (page - 1) * perPage

  return {
    items: filtered.slice(start, start + perPage),
    totalCount: filtered.length,
  }
}

export const fetchAdminReleaseNotes = async ({
  page = 1,
  perPage = 20,
  status,
} = {}) => {
  /* eslint-disable camelcase */
  const query = new URLSearchParams({
    page,
    per_page: perPage,
    ...(status && status !== 'all' ? { status } : {}),
  }).toString()
  /* eslint-enable camelcase */

  try {
    const result = await requestWithSegment(
      `/api/v2/admin/release_notes?${query}`
    )
    const items = Array.isArray(result) ? result : result?.release_notes
    if (Array.isArray(items)) {
      return {
        items: items.map(normalizeAdminReleaseNote),
        totalCount: result?.meta?.total_count ?? items.length,
      }
    }
    if (isDevelopment)
      return paginateMockAdminReleaseNotes({ page, perPage, status })

    throw new Error('Unexpected admin release notes response')
  } catch (e) {
    if (!isDevelopment) throw e

    return paginateMockAdminReleaseNotes({ page, perPage, status })
  }
}

export const exportAdminReleaseNotes = async (id) => {
  return requestBlobWithSegment(`/api/v2/admin/release_notes/${id}/export`)
}

function toApiPayload(formPayload) {
  /* eslint-disable camelcase */
  const payload = {}
  if (formPayload.title !== undefined) payload.title = formPayload.title
  if (formPayload.summary !== undefined)
    payload.description = formPayload.summary
  if (formPayload.body !== undefined) payload.text = formPayload.body
  if (formPayload.product_scope !== undefined)
    payload.product_scope = formPayload.product_scope
  if (formPayload.priority !== undefined)
    payload.priority = formPayload.priority
  if (formPayload.status !== undefined) payload.status = formPayload.status
  if (formPayload.audience_roles !== undefined)
    payload.audience_roles = formPayload.audience_roles
  if (formPayload.audience_segments !== undefined)
    payload.audience_segments = formPayload.audience_segments
  if (formPayload.feature_context !== undefined)
    payload.feature_context = formPayload.feature_context
  if (formPayload.audience_segments === undefined) {
    payload.audience_segments = RELEASE_SEGMENTS
  } else {
    payload.audience_segments = formPayload.audience_segments
  }

  if (formPayload.screenshot_sources !== undefined)
    payload.screenshot_sources = formPayload.screenshot_sources
  if (formPayload.screenshots !== undefined)
    payload.screenshots = formPayload.screenshots
  /* eslint-enable camelcase */
  return payload
}

export const createAdminReleaseNote = async (payload) => {
  return requestWithSegment('/api/v2/admin/release_notes', {
    method: 'POST',
    body: JSON.stringify({ [RELEASE_NOTE_PARAM]: toApiPayload(payload) }),
  })
}

export const createAiDraftRelease = async (payload) => {
  return requestWithSegment('/api/v2/admin/release_notes/ai_draft', {
    method: 'POST',
    body: JSON.stringify(toApiPayload(payload)),
  })
}

export const updateAdminReleaseNote = async (id, payload) =>
  requestWithSegment(`/api/v2/admin/release_notes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ [RELEASE_NOTE_PARAM]: toApiPayload(payload) }),
  })

export const publishAdminReleaseNote = async (id, payload = {}) =>
  requestWithSegment(`/api/v2/admin/release_notes/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const scheduleAdminReleaseNote = async (id, payload = {}) =>
  requestWithSegment(`/api/v2/admin/release_notes/${id}/schedule`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const archiveAdminReleaseNote = async (id) =>
  requestWithSegment(`/api/v2/admin/release_notes/${id}/archive`, {
    method: 'POST',
    body: JSON.stringify({}),
  })

export const uploadReleaseNoteScreenshot = async (file) => {
  const formData = new FormData()
  formData.append('file', file)

  const headers = releaseNotesHeaders()
  delete headers['Content-Type']

  const result = await api.request(
    '/api/v2/admin/release_notes/upload_screenshot',
    {
      method: 'POST',
      body: formData,
      headers: new Headers(headers),
    },
    config.redirectUri
  )
  return result.image_url
}
